// src/components/modals/DetailsModal/WordModalTabs/ExamplesTab.js
import React, { useState, useEffect, useRef } from 'react';
import { DocumentTextIcon } from '@heroicons/react/24/outline';
import SearchResultCard from '../../../search/SearchResultCard';
import ActionButton from '../../../common/ActionButton';
import { getSentencesByIds } from '../../../../firebase/words';
import { useMetadata } from '../../../../contexts/MetadataProvider';

const ExamplesTab = ({ exampleData = { sentenceIds: [], loadedSentences: [] }, userId, onItemClick, onSentencesChange }) => {
  const { learnedSentenceIds } = useMetadata();
  const [isLoading, setIsLoading] = useState(false);
  const scrollContainerRef = useRef(null);

  const { sentenceIds, loadedSentences } = exampleData;

  useEffect(() => {
    if (sentenceIds.length > 0 && loadedSentences.length === 0) {
      loadInitialSentences();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sentenceIds]);

  const loadInitialSentences = async () => {
    if (sentenceIds.length === 0) return;

    setIsLoading(true);
    const initialIds = sentenceIds.slice(0, 5);
    const sentences = await getSentencesByIds(initialIds, userId, learnedSentenceIds);
    onSentencesChange(sentences);
    setIsLoading(false);
  };

  const handleSentenceClick = (sentence) => {
    if (onItemClick) {
      onItemClick(sentence);
    }
  };

  const handleLoadMore = async () => {
    if (isLoading) return;

    setIsLoading(true);
    const currentLength = loadedSentences.length;
    const nextIds = sentenceIds.slice(currentLength, currentLength + 5);
    const newSentences = await getSentencesByIds(nextIds, userId, learnedSentenceIds);
    onSentencesChange([...loadedSentences, ...newSentences]);
    setIsLoading(false);

    // Scroll to the first newly loaded sentence
    setTimeout(() => {
      if (scrollContainerRef.current) {
        const sentences = scrollContainerRef.current.children;
        if (sentences[currentLength]) {
          sentences[currentLength].scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }
    }, 100);
  };

  return (
    <div className="relative p-6">
      {/* Background decor can remain as it's part of the tab's style */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute rounded-full backdrop-blur-sm border border-white/5"
          style={{
            background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.07), rgba(236, 72, 153, 0.04))',
            width: '100px',
            height: '100px',
            top: '5%',
            right: '10%',
            boxShadow: '0 8px 32px rgba(59, 130, 246, 0.02)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-30 rounded-full" />
        </div>
        <div
          className="absolute rounded-full backdrop-blur-sm border border-white/5"
          style={{
            background: 'linear-gradient(45deg, rgba(236, 72, 153, 0.06), rgba(139, 92, 246, 0.03))',
            width: '70px',
            height: '70px',
            bottom: '15%',
            left: '8%',
            boxShadow: '0 8px 32px rgba(236, 72, 153, 0.01)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-30 rounded-full" />
        </div>
      </div>

      {/* Content */}
      <div className="relative">
        {sentenceIds.length === 0 ? (
          <div className="text-center py-8">
            <DocumentTextIcon className="h-12 w-12 text-content-secondary/40 mx-auto mb-3" />
            <p className="text-content-secondary">No example sentences found.</p>
          </div>
        ) : (
          <>
            {isLoading && loadedSentences.length === 0 ? (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, index) => (
                  <div key={index} className="animate-pulse">
                    <div className="bg-gray-200 h-20 rounded-lg"></div>
                  </div>
                ))}
              </div>
            ) : (
              <>
                <div ref={scrollContainerRef} className="max-h-96 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 space-y-3 pl-1 pr-3 pt-3 pb-1">
                  {loadedSentences.map((sentence) => (
                    <SearchResultCard key={sentence.id} item={sentence} type="sentences" onClick={() => handleSentenceClick(sentence)} variant="light" learnedIds={learnedSentenceIds} />
                  ))}
                </div>
                {loadedSentences.length < sentenceIds.length && (
                  <div className="mt-4 text-center">
                    <ActionButton
                      onClick={handleLoadMore}
                      disabled={isLoading}
                      loading={isLoading}
                      variant="glassy"
                      color="orange"
                      className="px-6 py-2"
                    >
                      Load More ({sentenceIds.length - loadedSentences.length} remaining)
                    </ActionButton>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ExamplesTab;